import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Heart } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { getSavedProperties } from '@/lib/api';
import CurrencySwitcher from '@/components/CurrencySwitcher';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [savedCount, setSavedCount] = useState(0);
  const location = useLocation();

  // Pages with dark hero backgrounds where white text works
  const pagesWithDarkHero = ['/'];
  const hasDarkHero = pagesWithDarkHero.includes(location.pathname);
  
  // Use dark text when: scrolled OR on pages without dark hero
  const useDarkText = isScrolled || !hasDarkHero;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const fetchSaved = async () => {
      try {
        const res = await getSavedProperties();
        setSavedCount(res.data.saved_ids?.length || 0);
      } catch (e) {
        if (process.env.NODE_ENV !== 'production') console.error('Saved count fetch failed:', e);
      }
    };
    fetchSaved();
    
    // Listen for saved property changes
    const handleSavedChange = () => fetchSaved();
    window.addEventListener('savedPropertyChange', handleSavedChange);
    return () => window.removeEventListener('savedPropertyChange', handleSavedChange);
  }, []);

  const navLinks = [
    { href: '/', label: 'Home' },
    { href: '/regions', label: 'Regions' },
    { href: '/property-management', label: 'Property Management' },
    { href: '/real-estate', label: 'Real Estate' },
    { href: '/blog', label: 'Blog' },
    { href: '/about', label: 'About' },
    { href: '/contact', label: 'Contact' },
  ];

  const isActive = (path) => {
    if (path === '/blog') return location.pathname.startsWith('/blog');
    if (path === '/regions') return location.pathname.startsWith('/region');
    return location.pathname === path;
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled || !hasDarkHero ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-transparent'
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center flex-shrink-0" data-testid="navbar-logo">
            <span className={`font-heading text-2xl font-semibold tracking-tight ${
              useDarkText ? 'text-charcoal' : 'text-white'
            }`}>
              Lalita
            </span>
            <span className={`font-heading text-2xl font-normal ml-1 ${
              useDarkText ? 'text-ubud-jungle' : 'text-white/80'
            }`}>
              Properties
            </span>
          </Link>

          {/* Right cluster — nav links + actions, kept tight together */}
          <div className="hidden xl:flex items-center gap-8">
            {/* Desktop Navigation */}
            <div className="flex items-center gap-7">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`font-body text-sm font-medium transition-colors link-underline whitespace-nowrap ${
                    useDarkText
                      ? isActive(link.href)
                        ? 'text-ubud-jungle'
                        : 'text-charcoal-light hover:text-charcoal'
                      : isActive(link.href)
                      ? 'text-white'
                      : 'text-white/80 hover:text-white'
                  }`}
                  data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* Right-side actions, visually separated from links by a subtle divider */}
            <div className={`flex items-center gap-3 pl-6 border-l ${useDarkText ? 'border-gray-200' : 'border-white/20'}`}>
              <CurrencySwitcher variant="navbar" />
            <Link
              to="/saved"
              className={`relative p-2 rounded-full transition-colors ${
                useDarkText ? 'hover:bg-warm-sand' : 'hover:bg-white/10'
              }`}
              data-testid="nav-saved-btn"
            >
              <Heart className={`w-5 h-5 ${useDarkText ? 'text-charcoal' : 'text-white'}`} />
              {savedCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-ubud-jungle text-white text-xs font-medium rounded-full flex items-center justify-center">
                  {savedCount}
                </span>
              )}
            </Link>
            <Link
              to="/contact"
              className="bg-ubud-jungle text-white hover:bg-ubud-jungle-dark transition-all duration-300 rounded-full px-6 py-2.5 font-body text-sm font-medium"
              data-testid="nav-list-property-btn"
            >
              List Your Property
            </Link>
            </div>
          </div>

          {/* Mobile: currency switcher stays visible alongside hamburger */}
          <div className="flex xl:hidden items-center gap-2">
            <CurrencySwitcher variant="navbar" />
            <button
              onClick={() => setIsOpen(!isOpen)}
              className={`p-2 rounded-lg ${
                useDarkText ? 'text-charcoal hover:bg-warm-sand' : 'text-white hover:bg-white/10'
              }`}
              data-testid="mobile-menu-btn"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="xl:hidden bg-white border-t"
          >
            <div className="px-4 py-4 space-y-2">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setIsOpen(false)}
                  className={`block px-4 py-3 rounded-lg font-body text-sm ${
                    isActive(link.href)
                      ? 'bg-warm-sand text-ubud-jungle font-medium'
                      : 'text-charcoal hover:bg-warm-sand'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <Link
                to="/saved"
                onClick={() => setIsOpen(false)}
                className="flex items-center gap-2 px-4 py-3 rounded-lg text-charcoal hover:bg-warm-sand"
              >
                <Heart className="w-5 h-5" />
                <span>Saved ({savedCount})</span>
              </Link>
              <Link
                to="/contact"
                onClick={() => setIsOpen(false)}
                className="block w-full text-center bg-ubud-jungle text-white rounded-full py-3 font-medium mt-4"
              >
                List Your Property
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
